import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import MainHeader from '@/components/MainHeader';
import Scoreboard from '@/components/Scoreboard';
import Penalties from '@/components/Penalties';
import Shootout from '@/components/Shootout';
import ThreeStars from '@/components/ThreeStars';
import DisplayTab from '@/components/Tabs/Display';
import Widget from '@/components/Widget';
import Boxscore from '@/components/Boxscore';
import GameEvents from '@/components/GameEvents';
import ScoringSummary from '@/components/ScoringSummary';
import PenaltySummary from '@/components/PenaltySummary';
import GameStats from '@/components/GameStats';
import Attendance from '@/components/Attendance';
import Income from '@/components/Income';
import Referees from '@/components/Referees';
import GameNotes from '@/components/GameNotes';


const useStyles = makeStyles(theme => ({
  eventsContainer: {
    order: 'unset',
    [theme.breakpoints.down('sm')]: {
      order: 1,
    },
  },
  container: {
    order: 'unset',
    [theme.breakpoints.down('sm')]: {
      order: 2,
    },
  },
  footer: {
    order: 'unset',
    [theme.breakpoints.down('sm')]: {
      order: 3,
    },
  }
}));

function shouldShowPenalties(clock, period){
  const penaltiesTotal = Object.values(clock.penalties)
    .reduce((arr, item) => [...item, ...arr], []);

  if(period.isFinal){
    return false;
  }

  if(period.isShootout){
    return false;
  }

  if(!penaltiesTotal.length){
    return false;
  }

  return true;
}

function GameView({
  clock,
  date,
  events,
  goalies,
  goals,
  isFinal,
  isPaused,
  notes,
  officials,
  onPause,
  onSpeedChange,
  output,
  penalties,
  period,
  periods,
  shootout,
  skaters,
  speed,
  stats,
  teams,
  threeStars,
  venue
}) {
  const classes = useStyles();
  const [displayType, setDisplayType] = useState('live');
  const players = { ...skaters, ...goalies };

  return (
    <Grid container alignContent="flex-start" spacing={5}>
      <Grid item xs={12}>
        <MainHeader date={date} isPaused={isPaused} onSpeedChange={onSpeedChange} speed={speed} output={output} onPause={onPause} />
      </Grid>
      <Grid item xs={12}>
        <Scoreboard clock={clock} period={period} teams={teams} stats={stats.teams} />
      </Grid>
      {shouldShowPenalties(clock, period) && (
        <Grid item xs={12}>
          <Penalties teams={teams} penalties={clock.penalties} players={players} />
        </Grid>
      )}
      {shootout.isActive && (
        <Grid item xs={12}>
          <Shootout teams={teams} shootout={shootout} skaters={skaters} />
        </Grid>
      )}
      {isFinal && (
        <Grid item xs={12}>
          <Widget title="Three Stars">
            <ThreeStars threeStars={threeStars} teams={teams} players={players} />
          </Widget>
        </Grid>
      )}
      <Grid item xs={12}>
        <DisplayTab onChange={setDisplayType} value={displayType} />
      </Grid>
      {displayType === 'live' && (
        <>
          <Grid
            item
            container
            alignContent="flex-start"
            className={classes.container}
            spacing={3}
            xs={12}
            md={3}
          >
            <Grid item xs={12}>
              <Widget noPadding title="Scoring Summary">
                <ScoringSummary goals={goals} periods={periods} players={players} teams={teams} />
              </Widget>
            </Grid>
            <Grid item xs={12}>
              <Widget noPadding title="Penalty Summary">
                <PenaltySummary penalties={penalties} periods={periods} players={players} teams={teams} />
              </Widget>
            </Grid>
          </Grid>
          <Grid item className={classes.eventsContainer} xs={12} md={6}>
            <Widget title="Events">
              <GameEvents events={events} teams={teams} players={players} periods={periods} />
            </Widget>
          </Grid>
          <Grid item className={classes.container} xs={12} md={3}>
            <Widget title="Game Stats">
              <GameStats teams={teams} stats={stats.teams} />
            </Widget>
          </Grid>
        </>
      )}
      {displayType === 'boxscore' && (
        <>
          <Grid item xs={12}>
            <Widget>
              <Boxscore
                goalies={stats.goalies}
                isFinal={isFinal}
                skaters={stats.skaters}
                teams={teams}
              />
            </Widget>
          </Grid>
        </>
      )}
      {displayType === 'other' && (
        <>
          <Grid item xs={12} sm={6}>
            <Widget title="Notes">
              <GameNotes notes={notes} />
            </Widget>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Widget title="Officials">
              <Referees officials={officials} />
            </Widget>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Widget title="Attendance">
              <Attendance venue={venue.rink} attendance={venue.attendance} />
            </Widget>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Widget title="Income">
              <Income income={venue.income} />
            </Widget>
          </Grid>
        </>
      )}
      <Grid container item className={classes.footer} justify="flex-end" xs={12}>
        <Typography variant="caption">v.{process.env.REACT_APP_BUILD_NO}</Typography>
      </Grid>
    </Grid>
  );
}

export default GameView;
